import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';

import { Button, Loader, Grid, Cell } from '@teamsnap/teamsnap-ui';

import * as CreateProgram from '../../core/gql/mutations/create-program';
import * as UpdateProgram from '../../core/gql/mutations/update-program';
import * as QueryProgram from '../../core/gql/queries/program';
import * as QueryPrograms from '../../core/gql/queries/programs';
import * as ShowOrganization from '../../core/gql/queries/show-organizations';
import * as QueryTags from '../../core/gql/queries/tags';
import { isQueryFinished } from '../../core/apollo-helpers';

import FormElementHook from '../../components/form/form-element-hook';

import { TagType, unarchivedOrgTagsForType, selectableOrgTagsForType } from '../../models/tag';
import DivisionAttribute, { buildCheckboxName } from '../../components/program/select-division-attributes';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import { APP_URL } from '../../core/constants';
import ProgramTypes from './program-guide/program-types';
import { trackChurnZeroEvent } from 'components/common/useChurnZero';

const CreateElement = FormElementHook as new () => FormElementHook;

const updateValuesForDivisionAttributes = (values) => {
  ['ageDivisions', 'competitionGenders', 'competitionLevels'].forEach((attributeName) => {
    if (values[attributeName]) {
      values[attributeName] = Object.values(values[attributeName])
        .filter((v) => v)
        .map((id) => parseInt(id as string));
    }
  });

  return values;
};

const tooltip = (organizationId) => (
  <div style={{ width: '320px', textAlign: 'left' }}>
    <p>
      For every gender, age, and competition level selected a corresponding division will be created automatically when
      a new season is added to this program.
    </p>
    <p>
      If you wish to change these attributes for the program, please visit{' '}
      <a href={`${APP_URL}/organizations/${organizationId}/settings`}>Org Settings</a> and update values under the
      Program Attributes tabs.{' '}
      <a
        href="https://helpme.teamsnap.com/article/1705-how-program-attributes-are-used"
        target="_blank"
        rel="noreferrer"
      >
        Learn More
      </a>
    </p>
  </div>
);

interface Props {
  programId: number | null;
  organizationId: number;
}

const ProgramEditForm: React.FunctionComponent<Props> = ({ programId, organizationId }) => {
  const history = useHistory();
  const mutationOptions = {
    refetchQueries: programId
      ? [{ query: QueryProgram.QUERY, variables: { programId: programId } }]
      : [{ query: QueryPrograms.QUERY, variables: { organizationId: organizationId } }],
  };
  const [createProgram, createProgramResponse] = useMutation(CreateProgram.MUTATION, mutationOptions);
  const [updateProgram, updateProgramResponse] = useMutation(UpdateProgram.MUTATION, mutationOptions);

  const programEditForm = useForm();

  const setupFormDefaults = React.useCallback((queryProgramData, queryOrgData, queryTagData) => {
    const possibleAreasOfOperation = unarchivedOrgTagsForType(queryTagData?.tags, TagType.AREA_OF_OPERATION);

    const existingCompGenders = queryProgramData?.showProgram?.competitionGenders || [];
    const existingAgeDivisions = queryProgramData?.showProgram?.ageDivisions || [];
    const existingCompLevels = queryProgramData?.showProgram?.competitionLevels || [];
    const areaOfOperation =
      queryProgramData?.showProgram?.areaOfOperation?.id ||
      (possibleAreasOfOperation.length == 1 ? possibleAreasOfOperation[0].id : '');

    Object.entries(
      Object.assign(
        {
          name: queryProgramData?.showProgram.name,
          type: queryProgramData?.showProgram.type,
          description: queryProgramData?.showProgram.description,
          ageDemographic: queryProgramData?.showProgram.ageDemographic,
          isCoed: queryProgramData?.showProgram?.isCoed,
          sport: queryProgramData?.showProgram?.sportId || queryOrgData?.showOrganization?.sport,
          areaOfOperation: areaOfOperation,
        },
        ...existingCompGenders.map((tag) => ({ [buildCheckboxName(tag, 'competitionGenders')]: true })),
        ...existingAgeDivisions.map((tag) => ({ [buildCheckboxName(tag, 'ageDivisions')]: true })),
        ...existingCompLevels.map((tag) => ({ [buildCheckboxName(tag, 'competitionLevels')]: true }))
      )
    ).forEach(([key, value]) => {
      programEditForm.setValue(key, value);
    });
  }, []);

  const queryOrganization = useQuery(ShowOrganization.QUERY, { variables: { organizationId: organizationId } });
  const queryProgram = useQuery(QueryProgram.QUERY, {
    variables: { programId: programId },
    skip: !programId,
  });
  const loadOrgTagsResponse = useQuery(QueryTags.QUERY, {
    variables: { organizationId: organizationId },
  });

  const isSubmitting = createProgramResponse.loading || updateProgramResponse.loading;
  const { register, handleSubmit } = programEditForm;

  React.useEffect(() => {
    setupFormDefaults(queryProgram.data, queryOrganization.data, loadOrgTagsResponse.data);
  }, [
    queryProgram.called,
    queryProgram.loading,
    loadOrgTagsResponse.called,
    loadOrgTagsResponse.loading,
    queryOrganization.called,
    queryOrganization.loading,
  ]);

  const submit = async (values) => {
    updateValuesForDivisionAttributes(values);
    const isCreate = !programId;
    const variables = {
      ...values,
      organizationId: organizationId,
      sportId: +values.sport,
      areaOfOperation: values.areaOfOperation ? parseInt(values.areaOfOperation) : null,
    };

    let feedbackMessage: string;

    try {
      if (isCreate) {
        const resp = await createProgram({ variables });

        if (resp.data && resp.data.createProgram) {
          feedbackMessage = `The program ${resp.data.createProgram.name} has been created.`;
          trackChurnZeroEvent('Gemini - Program Created', queryOrganization?.data?.showOrganization);
        }
      } else {
        const resp = await updateProgram({ variables: { ...variables, id: programId } });
        feedbackMessage = `The program ${resp.data.updateProgram.name} has been updated.`;
      }

      reactiveFeedbackMessage({
        message: feedbackMessage,
        isError: false,
        autoclose: true,
      });
      hide(true);
    } catch (e) {
      const errorMessage = isCreate
        ? 'Program create failed. Please try again.'
        : 'Program update failed. Please try again.';
      reactiveFeedbackMessage({
        message: errorMessage,
        isError: true,
        autoclose: true,
      });
      console.warn('Failed to properly validate and submit program and season forms.');
    }
  };

  const hide = (updated?: boolean) => {
    history.push(`/organizations/${organizationId}/programs`);
  };

  // UTILS
  const getAgeDemographicOptions = () => [
    { text: 'Youth', value: 'YOUTH' },
    { text: 'Adult', value: 'ADULT' },
  ];

  const getAreaOfOperationOptions = () => {
    let options = selectableOrgTagsForType(
      loadOrgTagsResponse.data?.tags,
      TagType.AREA_OF_OPERATION,
      queryProgram?.data?.showProgram?.areaOfOperation ? [queryProgram?.data?.showProgram?.areaOfOperation] : []
    ).map((area) => ({ text: area.name + (area.isArchived ? ' (Archived)' : ''), value: `${area.id}` }));

    options = [{ text: 'Select an area of operation', value: '' }, ...options];

    return options;
  };

  const headerText = programId ? 'Edit Program' : 'New Program';
  const possibleAgeDivision = selectableOrgTagsForType(
    loadOrgTagsResponse.data?.tags,
    TagType.AGE_DIVISION,
    queryProgram?.data?.showProgram?.ageDivisions || []
  );
  const possibleCompetitionGenders = selectableOrgTagsForType(
    loadOrgTagsResponse.data?.tags,
    TagType.COMPETITION_GENDER,
    queryProgram?.data?.showProgram?.competitionGenders || []
  );
  const possibleCompetitionLevels = selectableOrgTagsForType(
    loadOrgTagsResponse.data?.tags,
    TagType.COMPETITION_LEVEL,
    queryProgram?.data?.showProgram?.competitionLevels || []
  );

  const readyToShow = !queryProgram.loading && isQueryFinished(loadOrgTagsResponse);

  return (
    <div className="u-maxWidthSm">
      {readyToShow && (
        <div id="program-create-editor-content" className="page-content u-xs-padRightMd u-xs-padLeftMd">
          <div className="page-content-header Grid u-flexAlignItemsCenter">
            <div className="Grid-cell u-xs-size1of2">
              <h2>{headerText}</h2>
            </div>
          </div>
          <form onSubmit={handleSubmit(submit)}>
            <Cell mods="u-size12of12">
              <CreateElement
                label="Name"
                useHookResponse={programEditForm}
                inputRef={register({
                  required: 'You must enter a name for the program.',
                })}
                field="name"
                type="text"
                placeholder="Program name"
              />
            </Cell>

            <Cell mods="u-size12of12 u-padTopMd">
              <CreateElement
                label="Description"
                useHookResponse={programEditForm}
                inputRef={register({
                  required: false,
                })}
                field="description"
                type="text"
                placeholder="Program description"
              />
            </Cell>

            <Grid isWithGutter={true} mods="u-padTopMd">
              <Cell mods="u-size6of12">
                <CreateElement
                  label="Sport"
                  useHookResponse={programEditForm}
                  inputRef={register({
                    required: 'You must select a sport',
                  })}
                  field="sport"
                  type="sport"
                />
              </Cell>

              <Cell mods="u-size6of12">
                <CreateElement
                  label="Area of Operation"
                  useHookResponse={programEditForm}
                  inputRef={register({
                    required: 'You must select an area of operation',
                  })}
                  field="areaOfOperation"
                  type="select"
                  options={getAreaOfOperationOptions()}
                />
              </Cell>
            </Grid>

            <Grid isWithGutter={true} mods="u-padTopMd u-flexAlignItemsCenter">
              <Cell mods="u-size6of12">
                <CreateElement
                  useHookResponse={programEditForm}
                  field="isCoed"
                  inputRef={register({
                    required: false,
                  })}
                  type="checkbox"
                  label="Track These As Coed Teams"
                  isInline={true}
                />
              </Cell>

              <Cell mods="u-size6of12">
                <CreateElement
                  label="Program Type"
                  useHookResponse={programEditForm}
                  inputRef={register({
                    required: 'You must select a program type',
                  })}
                  field="type"
                  type="select"
                  options={ProgramTypes.map(({ id, label }) => ({ text: label, value: id }))}
                />
              </Cell>

              <Cell mods="u-size6of12">
                <CreateElement
                  className="u-hidden"
                  labelHidden={true}
                  label="Age Demographic"
                  useHookResponse={programEditForm}
                  field="ageDemographic"
                  inputRef={register({
                    required: false,
                  })}
                  type="select"
                  options={getAgeDemographicOptions()}
                />
              </Cell>
            </Grid>

            <DivisionAttribute
              attributeTags={possibleCompetitionGenders}
              title="Competition Genders"
              attributeName="competitionGenders"
              seasonForm={programEditForm}
              tooltip={tooltip(organizationId)}
            />

            <DivisionAttribute
              attributeTags={possibleAgeDivision}
              title="Age Divisions"
              attributeName="ageDivisions"
              seasonForm={programEditForm}
              tooltip={tooltip(organizationId)}
            />

            <DivisionAttribute
              attributeTags={possibleCompetitionLevels}
              title="Competition Levels"
              attributeName="competitionLevels"
              seasonForm={programEditForm}
              tooltip={tooltip(organizationId)}
            />

            <Cell mods="u-textLeft u-padTopMd u-padBottomXl u-spaceBottomXl">
              <div className="button-list">
                <Button mods="Button--negative" icon="dismiss" onClick={hide}>
                  Cancel
                </Button>
                <Button isDisabled={isSubmitting} type="submit" color="primary" icon={isSubmitting ? null : 'check'}>
                  {isSubmitting && <Loader type="spin" style={{ width: '13px', height: '13px' }} />}
                  {programId ? 'Update' : 'Create'} Program
                </Button>
              </div>
            </Cell>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProgramEditForm;
